/**
 * Line icons of the Admin Ultimate Design (어드민 궁극의 디자인, 2026-10-17): one stroke weight, drawn in the
 * text colour, so a button reads the same on every desk screen. They are decoration only; the button's own
 * words carry its name, so every icon is hidden from assistive technology.
 */
import type { ReactElement, SVGProps } from 'react';

type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'>;

const base = (paths: ReactElement, { className, ...props }: IconProps): ReactElement => (
  <svg
    viewBox="0 0 24 24"
    className={['ff-icon', className].filter(Boolean).join(' ')}
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    {paths}
  </svg>
);

export const SearchIcon = (props: IconProps) =>
  base(
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m16 16 5 5" />
    </>,
    props,
  );

export const ScanIcon = (props: IconProps) =>
  base(<path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M7 7v10m3-10v10m4-10v10m3-10v10M2 12h20" />, props);

export const PrintIcon = (props: IconProps) =>
  base(<path d="M6 9V3h12v6M6 18H3V9h18v9h-3M6 14h12v7H6zM17 11h1" />, props);

export const CommentIcon = (props: IconProps) =>
  base(
    <>
      <path d="M4 4h16v12H9l-5 5z" />
      <path d="M8 8h8m-8 4h5" />
    </>,
    props,
  );

export const GearIcon = (props: IconProps) =>
  base(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>,
    props,
  );

export const RefreshIcon = (props: IconProps) =>
  base(
    <>
      <path d="M20 12a8 8 0 1 1-2.3-5.7" />
      <path d="M20 4v5h-5" />
    </>,
    props,
  );

export const DownloadIcon = (props: IconProps) => base(<path d="M12 3v12m-5-5 5 5 5-5M4 17v3h16v-3" />, props);

export const UploadIcon = (props: IconProps) => base(<path d="M12 15V3m-5 5 5-5 5 5M4 17v3h16v-3" />, props);

export const BoxIcon = (props: IconProps) =>
  base(
    <>
      <path d="m3 8 9-4 9 4v9l-9 4-9-4z" />
      <path d="m3 8 9 4 9-4M12 12v9" />
    </>,
    props,
  );

export const WarningIcon = (props: IconProps) =>
  base(
    <>
      <path d="M12 3 2 21h20z" />
      <path d="M12 10v5m0 3h.01" />
    </>,
    props,
  );

/* Order Detail (SSD-7441): the actions on an order and its section headings. */

export const HistoryIcon = (props: IconProps) =>
  base(
    <>
      <path d="M3 12a9 9 0 1 0 2.6-6.4L3 8" />
      <path d="M3 3v5h5M12 7v5l3 2" />
    </>,
    props,
  );

export const ExternalIcon = (props: IconProps) =>
  base(
    <>
      <path d="M14 4h6v6M20 4l-9 9" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </>,
    props,
  );

export const CloneIcon = (props: IconProps) =>
  base(
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
    </>,
    props,
  );

export const ReceiptIcon = (props: IconProps) =>
  base(<path d="M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6M9 16h3" />, props);

export const CloseIcon = (props: IconProps) => base(<path d="M6 6l12 12M18 6 6 18" />, props);

/** A credited box, a cleared row: the mark a verdict badge carries instead of the ✓ glyph (Closing, SSD-7455). */
export const CheckIcon = (props: IconProps) => base(<path d="m5 12.5 4.5 4.5L19 7" />, props);

/** Something the operator expected did not happen: the mark of an error notification (SSD-7705). */
export const ErrorIcon = (props: IconProps) =>
  base(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V13m0 3.5h.01" />
    </>,
    props,
  );

/** A scanner that stopped listening: the mark of the idle badge instead of the ⏸ glyph (Closing, SSD-7455). */
export const PauseIcon = (props: IconProps) => base(<path d="M8.5 5v14M15.5 5v14" />, props);

export const PlusIcon = (props: IconProps) => base(<path d="M12 5v14M5 12h14" />, props);

export const CameraIcon = (props: IconProps) =>
  base(
    <>
      <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
      <circle cx="12" cy="13" r="3.5" />
    </>,
    props,
  );

export const PencilIcon = (props: IconProps) =>
  base(
    <>
      <path d="M4 20h4L19 9l-4-4L4 16z" />
      <path d="m13.5 6.5 4 4" />
    </>,
    props,
  );

export const SwapIcon = (props: IconProps) => base(<path d="M4 8h14l-4-4M20 16H6l4 4" />, props);

export const ClipboardIcon = (props: IconProps) =>
  base(
    <>
      <path d="M9 4H6a1 1 0 0 0-1 1v15a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-3" />
      <rect x="9" y="2.5" width="6" height="3.5" rx="1" />
      <path d="M8.5 11h7M8.5 15h5" />
    </>,
    props,
  );

export const UserIcon = (props: IconProps) =>
  base(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>,
    props,
  );

export const TruckIcon = (props: IconProps) =>
  base(
    <>
      <path d="M2 6h12v10H2zM14 10h4l3 3v3h-7" />
      <circle cx="6.5" cy="17.5" r="2" />
      <circle cx="17.5" cy="17.5" r="2" />
    </>,
    props,
  );

export const ListIcon = (props: IconProps) =>
  base(<path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />, props);

export const LockIcon = (props: IconProps) =>
  base(
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>,
    props,
  );

export const ClockIcon = (props: IconProps) =>
  base(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>,
    props,
  );

export const VideoIcon = (props: IconProps) =>
  base(
    <>
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="m16 10 5-3v10l-5-3" />
    </>,
    props,
  );

/* Inventory (SSD-7472): the (?) beside a section title that opens what the section is for. */

export const HelpIcon = (props: IconProps) =>
  base(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.4a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.1-2.4 3.7M12 17h.01" />
    </>,
    props,
  );
